import {type DocumentNode, gql} from '@apollo/client';
import {
    type Filter,
    type GqlDownloadRequestPayload,
    type GqlRequest,
    GqlRequestBuilder,
    type IDownloadByLinkRequestBuilder,
    maxServerDownloadPageSize,
} from '@igp/shared/api';
import type {QueryExportCountryLevelReportArgs, QueryGetCountryLevelReportArgs} from '@igp/shared/contract/pam';
import type {EntityType} from '@models';

import type {
    CountryLevelPartnerReportDownloadFields,
    CountryLevelPartnerReportQueryFilterKeys,
    CountryLevelPartnerReportQueryKeys,
} from './types';

export class CountryLevelPartnerReportRequestBuilder
    extends GqlRequestBuilder<
        EntityType,
        QueryGetCountryLevelReportArgs,
        CountryLevelPartnerReportQueryKeys,
        CountryLevelPartnerReportQueryFilterKeys
    >
    implements IDownloadByLinkRequestBuilder<QueryExportCountryLevelReportArgs, CountryLevelPartnerReportDownloadFields>
{
    buildDownloadRequest(
        downloadRequestPayload: GqlDownloadRequestPayload<CountryLevelPartnerReportDownloadFields>
    ): GqlRequest<QueryExportCountryLevelReportArgs> {
        const filter = this.mapToFilterObject(downloadRequestPayload.filter);
        const queryArgs = this.getGqlPageArgs(filter);
        return {
            query: this.buildExportQuery(),
            variables: {
                report_name: downloadRequestPayload.report_name,
                filter: queryArgs?.filter,
                sort: queryArgs?.sort,
                start: 0,
                end: maxServerDownloadPageSize,
                fields: downloadRequestPayload.columns.map(column => ({
                    column_name: column.name,
                    field: column.field,
                })),
            },
        };
    }

    protected buildQuery(fields: CountryLevelPartnerReportQueryKeys[]): DocumentNode {
        const requiredFields: CountryLevelPartnerReportQueryKeys[] = ['items.date_ts.seconds', 'items.country', 'total_count'];
        const queryFields: CountryLevelPartnerReportQueryKeys[] = [...new Set([...fields, ...requiredFields])];
        return gql`
            query GetCountryLevelReport($filter: CountryLevelReportingFilter, $sort: Sorting, $start: Int, $end: Int) {
                getCountryLevelReport(filter: $filter, sort: $sort, end: $end, start: $start) {
                    ${this.buildQueryFields(queryFields)}
                }
            }
        `;
    }

    protected buildFilter(filter: Filter<CountryLevelPartnerReportQueryFilterKeys>): QueryGetCountryLevelReportArgs {
        return {
            filter: {
                country: this.toGQLMultiselectFilter(filter, 'country'),
                date_ts: this.toGQLDateRange(
                    this.getLatestTimestamp(filter['date_from'] as number, filter['date_min'] as number),
                    filter['date_to']
                ),
            },
        };
    }

    protected validateFilters(_filter: Partial<Record<CountryLevelPartnerReportQueryFilterKeys, unknown>>): void {
        return;
    }

    private buildExportQuery() {
        return gql`
            query ExportCountryLevelReport(
                $filter: CountryLevelReportingFilter
                $report_name: String!
                $fields: [ReportField]
                $sort: Sorting
                $start: Int
                $end: Int
            ) {
                exportCountryLevelReport(
                    filter: $filter
                    report_name: $report_name
                    fields: $fields
                    sort: $sort
                    end: $end
                    start: $start
                ) {
                    url
                }
            }
        `;
    }
}
